/**
 * The code in this file is derived from:
 * https://github.com/apollographql/react-apollo/blob/2.5.8/src/Query.tsx
 *
 * Edits have been made to remove functionality that we do not use or have
 * migrated to other mechanisms (as is the case with SSR-related things).
 */

// eslint-disable-next-line no-restricted-imports
import {
    ApolloError,
    type ApolloClient,
    type ApolloQueryResult,
    type FetchMoreOptions,
    type FetchMoreQueryOptions,
    // @ts-expect-error - TS2614 - Module '"apollo-client"' has no exported member 'ModifiableWatchQueryOptions'. Did you mean to use 'import ModifiableWatchQueryOptions from "apollo-client"' instead?
    type ModifiableWatchQueryOptions,
    // @ts-expect-error - TS2614 - Module '"apollo-client"' has no exported member 'NetworkStatusRaw'. Did you mean to use 'import NetworkStatusRaw from "apollo-client"' instead?
    type NetworkStatusRaw,
    type ObservableQuery,
    type WatchQueryOptions,
} from "apollo-client";
import type {DocumentNode} from "graphql";
import isEqual from "lodash.isequal";
import * as React from "react";

import {graphQLDocumentNodeParser} from "@khanacademy/wonder-blocks-data";
import {
    withActionScheduler,
    type WithActionSchedulerProps,
} from "@khanacademy/wonder-blocks-timing";
import type {FetchPolicy} from "@khan/data-gql-apollo";
import {Errors, KAError} from "@khan/logging";

// >KA Copied from:
// https://github.com/apollographql/react-apollo/blob/3bc993b2ea91704bd6a2667f42d1940656c071ff/src/utils/shallowEqual.ts
function is(x: any, y: any): boolean {
    if (x === y) {
        return x !== 0 || y !== 0 || 1 / x === 1 / y;
    }
    return x !== x && y !== y;
}

function isObject(obj: any): boolean {
    return obj !== null && typeof obj === "object";
}

function shallowEqual(objA: any, objB: any): boolean {
    if (is(objA, objB)) {
        return true;
    }

    if (!isObject(objA) || !isObject(objB)) {
        return false;
    }

    const objKeys = Object.keys(objA);

    if (objKeys.length !== Object.keys(objB).length) {
        return false;
    }

    return objKeys.every(
        (key) =>
            Object.prototype.hasOwnProperty.call(objB, key) &&
            is(objA[key], objB[key]),
    );
}
//<KA

interface Subscription {
    closed: boolean;
    unsubscribe(): void;
}

/**
 * This is to fill in for the `!` operator in TypeScript that asserts
 * "this value cannot be null".
 */
function assertNotNullish<T>(value?: T | null): T {
    if (value == null) {
        throw new KAError(
            "Null value encountered when it was expected to be non-null",
            Errors.Internal,
        );
    }
    return value;
}

interface AllFetchMoreOptions<TData, TVariables>
    extends FetchMoreOptions<TData, TVariables>,
        // @ts-expect-error - TS2314 - Generic type 'FetchMoreQueryOptions<TVariables, K>' requires 2 type argument(s).
        FetchMoreQueryOptions<TVariables> {}

export type QueryResult<TData, TVariables extends Record<any, any>> = {
    data?: TData;
    loading: boolean;
    error?: ApolloError;
    networkStatus: NetworkStatusRaw;
    refetch: (variables?: TVariables) => Promise<ApolloQueryResult<TData>>;
    fetchMore: (
        options: AllFetchMoreOptions<TData, TVariables>,
    ) => Promise<ApolloQueryResult<TData>>;
};

function observableQueryFields<TData>(observable: ObservableQuery<TData>) {
    const fields = {
        refetch: observable.refetch.bind(observable),
        fetchMore: observable.fetchMore.bind(observable),
    } as const;
    return fields;
}

type Context = Record<string, any>;

type OwnProps<TData, TVariables> = {
    // @ts-expect-error - TS2344
    children: (result: QueryResult<TData, TVariables>) => React.ReactNode;
    query: DocumentNode;
    skip?: boolean;
    onCompleted?: (data: TData) => void | null | undefined;
    onError?: (error: ApolloError) => void | null | undefined;
    variables?: TVariables;
    fetchPolicy?: FetchPolicy;
    pollInterval?: number;
    notifyOnNetworkStatusChange?: boolean;
    context?: Context;
    client: ApolloClient<any>;
};

type Props<TData, TVariables> = OwnProps<TData, TVariables> &
    WithActionSchedulerProps;

class ApolloQueryCore<
    TData extends Record<any, any>,
    TVariables extends Record<any, any> = Empty,
> extends React.Component<Props<TData, TVariables>> {
    // request / action storage. Note that we delete querySubscription if we
    // unsubscribe but never delete queryObservable once it is created. We
    // only delete queryObservable when we unmount the component.
    _queryObservable: ObservableQuery<TData> | null | undefined | null;
    _querySubscription: Subscription | null | undefined;
    _refetcherQueue:
        | {
              args: any;
              resolve: (value?: any | Promise<any>) => void;
              reject: (reason?: any) => void;
          }
        | null
        | undefined;

    _hasMounted: boolean;
    _lastRenderedResult: QueryResult<TData, TVariables> | null | undefined;

    constructor(props: Props<TData, TVariables>) {
        super(props);

        this._hasMounted = false;
        this._lastRenderedResult = null;
        this.initializeQueryObservable(props);
    }

    componentDidMount() {
        this._hasMounted = true;
        if (this.props.skip) {
            return;
        }

        this.startQuerySubscription();
        if (this._refetcherQueue) {
            const {args, resolve, reject} = this._refetcherQueue;
            assertNotNullish(this._queryObservable)
                .refetch(args)
                .then(resolve)
                .catch(reject);
        }
    }

    shouldComponentUpdate(nextProps: Props<TData, TVariables>): boolean {
        // the next render wants to skip when the last one didn't
        if (!this.props.skip && nextProps.skip) {
            assertNotNullish(this._queryObservable).resetLastResults();
            // So we remove our current subscription.
            this.removeQuerySubscription();

            // And then say we need to update, because we do.
            // No need to start a new subscription because we're skipping!
            return true;
        }

        // The props do not appear to have changed.
        // Let's not update.
        if (shallowEqual(this.props, nextProps)) {
            return false;
        }

        // We have a new client, so our current subscription is not valid.
        if (this.props.client !== nextProps.client) {
            this.removeQuerySubscription();
            this._queryObservable = null;
        }

        // We have a new query, so our current subscription is not valid
        // and the result we have is not useful.
        if (this.props.query !== nextProps.query) {
            this._queryObservable?.resetLastResults();
            this.removeQuerySubscription();
        }

        // Let's update our query with the next props, and if we're not
        // skipping, start the subscription.
        this.updateQuery(nextProps);
        if (!nextProps.skip) {
            // And if we're not skipping
            this.startQuerySubscription();
        }

        // Now, we definitely want to update.
        return true;
    }

    componentDidUpdate(prevProps: Props<TData, TVariables>) {
        const isDiffRequest =
            !isEqual(prevProps.query, this.props.query) ||
            !isEqual(prevProps.variables, this.props.variables);
        if (isDiffRequest) {
            // If specified, `onError` / `onCompleted` callbacks are called here
            // after local cache results are loaded.
            this.handleErrorOrCompleted();
        }
    }

    componentWillUnmount() {
        this.removeQuerySubscription();
        this._hasMounted = false;
    }

    assertIsQuery(props: Props<TData, TVariables>): void {
        const operation = graphQLDocumentNodeParser(props.query);

        if (operation.type !== "query") {
            throw new KAError(
                `The <Query /> component requires a graphql query, but got a ${operation.type}.`,
                Errors.Internal,
            );
        }
    }

    extractModifiableWatchQueryOptionsFromProps(
        props: Props<TData, TVariables>,
    ): ModifiableWatchQueryOptions {
        this.assertIsQuery(props);
        const {fetchPolicy, pollInterval, variables} = props;

        return {
            fetchPolicy,
            pollInterval,
            variables,
        };
    }

    extractOptsFromProps(props: Props<TData, TVariables>): WatchQueryOptions {
        const modifiableOptions =
            this.extractModifiableWatchQueryOptionsFromProps(props);
        const {context, query} = props;
        const displayName = "ApolloQueryCore";

        return {
            ...modifiableOptions,
            query,
            context: context || {},
            metadata: {reactComponent: {displayName}},
        };
    }

    initializeQueryObservable(props: Props<TData, TVariables>) {
        const opts = this.extractOptsFromProps(props);
        if (!this._queryObservable) {
            this._queryObservable = this.props.client.watchQuery(opts);
        }
    }

    updateQuery(props: Props<TData, TVariables>) {
        // if we skipped initially, we may not have yet created the observable
        if (!this._queryObservable) {
            this.initializeQueryObservable(props);
        }

        assertNotNullish(this._queryObservable)
            .setOptions(this.extractModifiableWatchQueryOptionsFromProps(props))
            // The error will be passed to the child container, so we don't
            // need to log it here. We could conceivably log something if
            // an option was set. OTOH we don't log errors w/ the original
            // query. See https://github.com/apollostack/react-apollo/issues/404
            .catch(() => null);
    }

    startQuerySubscription: () => void = () => {
        // When the `Query` component receives new props, or when we explicitly
        // re-subscribe to a query using `resubscribeToQuery`, we start a new
        // subscription in this method. To avoid un-necessary re-renders when
        // receiving new props or re-subscribing, we track the full last
        // observable result so it can be compared against incoming new data.
        // We only trigger a re-render if the incoming result is different than
        // the stored `lastRenderedResult`.

        if (this._querySubscription) {
            return;
        }

        this._querySubscription = assertNotNullish(
            this._queryObservable,
        ).subscribe({
            next: (result) => {
                if (
                    this._lastRenderedResult &&
                    this._lastRenderedResult.loading === result.loading &&
                    this._lastRenderedResult.networkStatus ===
                        result.networkStatus &&
                    shallowEqual(this._lastRenderedResult.data, result.data)
                ) {
                    return;
                }

                this.updateCurrentData();
            },
            error: (error) => {
                this.resubscribeToQuery();

                if (!error.hasOwnProperty("graphQLErrors")) {
                    throw error;
                }
                this.updateCurrentData();
            },
        });
    };

    removeQuerySubscription: () => void = () => {
        if (this._querySubscription) {
            this._querySubscription.unsubscribe();
            delete this._lastRenderedResult;
            delete this._querySubscription;
        }
    };

    resubscribeToQuery() {
        this.removeQuerySubscription();

        const queryObservable = assertNotNullish(this._queryObservable);

        // Unfortunately, if `lastError` is set in the current
        // `queryObservable` when the subscription is re-created,
        // the subscription will immediately receive the error, which will
        // cause it to terminate again. To avoid this, we first clear
        // the last error/result from the `queryObservable` before re-starting
        // the subscription, and restore it afterwards (so the subscription
        // has a chance to stay open).
        const lastError = queryObservable.getLastError();
        const lastResult = queryObservable.getLastResult();
        queryObservable.resetLastResults();
        this.startQuerySubscription();
        Object.assign(queryObservable, {lastError, lastResult});
    }

    updateCurrentData: () => void = () => {
        // If specified, `onError` / `onCompleted` callbacks are called here
        // after a network based Query result has been received.
        this.handleErrorOrCompleted();

        // Force a rerender that goes through shouldComponentUpdate.
        if (this._hasMounted) {
            this.forceUpdate();
        }
    };

    handleErrorOrCompleted: () => void = () => {
        const queryObservable = assertNotNullish(this._queryObservable);
        const result = queryObservable.currentResult();

        const {data, loading, error} = result;
        const {onCompleted, onError} = this.props;
        if (onCompleted && !loading && !error) {
            // @ts-expect-error - TS2345 - Argument of type '{} | TData' is not assignable to parameter of type 'TData'.
            onCompleted(data);
        } else if (onError && !loading && error) {
            onError(error);
        }
    };

    getQueryResult: () => QueryResult<TData, TVariables> = () => {
        let result: QueryResult<TData, TVariables> = {
            data: Object.create(null),
        } as any;
        // Attach bound methods
        Object.assign(
            result,
            observableQueryFields(assertNotNullish(this._queryObservable)),
        );

        // When skipping a query (ie. we're not querying for data but still want
        // to render children), make sure the `data` is cleared out and
        // `loading` is set to `false` (since we aren't loading anything).
        if (this.props.skip) {
            result = {
                ...result,
                data: undefined,
                error: undefined,
                loading: false,
            };
        } else {
            const queryObservable = assertNotNullish(this._queryObservable);
            const currentResult = queryObservable.currentResult();
            const {loading, networkStatus, errors} = currentResult;
            let {error} = currentResult;

            // Until a set naming convention for networkError and graphQLErrors is
            // decided upon, we map errors (graphQLErrors) to the error props.
            if (errors && errors.length > 0) {
                error = new ApolloError({graphQLErrors: errors});
            }

            const {fetchPolicy} = this.props;
            Object.assign(result, {loading, networkStatus, error});

            const previousData = this._lastRenderedResult
                ? this._lastRenderedResult.data
                : ({} as any);

            if (loading) {
                // @ts-expect-error - TS2769
                Object.assign(result.data, previousData, currentResult.data);
            } else if (error) {
                Object.assign(result, {
                    data: (queryObservable.getLastResult() || {}).data,
                });
            } else if (
                fetchPolicy === "no-cache" &&
                Object.keys(currentResult.data).length === 0
            ) {
                // Make sure data pulled in by a `no-cache` query is preserved
                // when the components parent tree is re-rendered.
                result.data = previousData;
            } else {
                // @ts-expect-error - TS2769
                Object.assign(result.data, currentResult.data);
            }
        }

        // Handle race condition where refetch is called on child mount or later
        // Normal execution model:
        // render(loading) -> mount -> start subscription -> get data -> render(with data)
        //
        // SSR with synchronous refetch:
        // render(with data) -> refetch -> mount -> start subscription
        //
        // SSR with asynchronous refetch:
        // render(with data) -> mount -> start subscription -> refetch
        //
        // If a subscription has not started, then the synchronous call to refetch
        // must be made at a time when an active network request is being made, so
        // we ensure that the network requests are deduped, to avoid an
        // inconsistent UI state that displays different data for the current query
        // alongside a refetched query.
        //
        // Once the Query component is mounted and the subscription is made, we
        // always hit the network with refetch, since the components data will be
        // updated and a network request is not currently active.
        if (!this._querySubscription) {
            const oldRefetch = result.refetch;

            result.refetch = (args: unknown) => {
                if (this._querySubscription) {
                    // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'TVariables | undefined'.
                    return oldRefetch(args);
                }
                // @ts-expect-error - TS2558 - Expected 1 type arguments, but got 2.
                return new Promise<any, any>((r: unknown, f: unknown) => {
                    // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type '(value?: any) => void'. | TS2322 - Type 'unknown' is not assignable to type '(reason?: any) => void'.
                    this._refetcherQueue = {resolve: r, reject: f, args};
                });
            };
        }

        // When the component is done rendering stored query errors, we'll
        // remove those errors from the `ObservableQuery` query store, so they
        // aren't re-displayed on subsequent (potentially error free)
        // requests/responses.
        this.props.schedule.timeout(() => {
            const queryObservable = assertNotNullish(this._queryObservable);
            // @ts-expect-error - TS2339 - Property 'resetQueryStoreErrors' does not exist on type 'ObservableQuery<TData, OperationVariables>'.
            if (queryObservable.resetQueryStoreErrors != null) {
                // @ts-expect-error - TS2339 - Property 'resetQueryStoreErrors' does not exist on type 'ObservableQuery<TData, OperationVariables>'.
                queryObservable.resetQueryStoreErrors();
            } else {
                // @ts-expect-error - TS2341 - Property 'queryManager' is private and only accessible within class 'ObservableQuery<TData, TVariables>'.
                const {queryManager, queryId} = queryObservable;
                const queryStore = queryManager.queryStore.get(queryId);
                if (queryStore) {
                    queryStore.networkError = null;
                    queryStore.graphQLErrors = [];
                }
            }
        }, 0);

        this._lastRenderedResult = result;
        return result;
    };

    render(): React.ReactNode {
        const result = this.getQueryResult();

        return this.props.children(result);
    }
}

// Sadly, we lose the parameterization here.
// So we reapply it with some casting.
const ApolloQueryCoreWithActionScheduler = withActionScheduler(ApolloQueryCore);

export default <
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>(
    props: OwnProps<TData, TVariables>,
): React.ReactElement => <ApolloQueryCoreWithActionScheduler {...props} />;
